import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCw } from "lucide-react";

interface Props {
  /** Bumps with each new error so the cooldown re-arms on every failure. */
  attempt: number;
  onRetry: () => void;
  /** Base cooldown in seconds. Doubles per attempt, capped at `maxSeconds`. */
  baseSeconds?: number;
  maxSeconds?: number;
  className?: string;
  label?: string;
}

/**
 * "Try again" button with a per-attempt cooldown. Prevents a user (or a
 * stuck auto-recovery loop) from rapidly remounting a failing subtree
 * dozens of times per second — which both wastes bandwidth on chunk
 * refetches and flashes the error UI repeatedly. Cooldown grows with
 * each consecutive failure (3s → 6s → 12s → 15s cap).
 */
export function RetryButton({
  attempt,
  onRetry,
  baseSeconds = 3,
  maxSeconds = 15,
  className,
  label = "Try again",
}: Props) {
  const cooldown = Math.min(maxSeconds, baseSeconds * Math.pow(2, Math.max(0, attempt - 1)));
  const [remaining, setRemaining] = useState(cooldown);

  useEffect(() => {
    setRemaining(cooldown);
    const id = window.setInterval(() => {
      setRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, 1000);
    return () => window.clearInterval(id);
    // Re-arm whenever a new failure arrives.
  }, [attempt, cooldown]);

  const disabled = remaining > 0;
  return (
    <Button
      onClick={onRetry}
      disabled={disabled}
      className={`gap-2 ${className ?? ""}`}
      aria-live="polite"
    >
      <RotateCw className={`h-4 w-4 ${disabled ? "" : ""}`} />
      {disabled ? `${label} in ${remaining}s` : label}
    </Button>
  );
}
